import { useMemo, useState } from 'react';
import { navigate } from '../App';
import { getClientId, getStoredName, getStoredSpectator, storeName, storeSpectator } from '../storage';
import { useRecentEmoji } from '../recentEmoji';
import { useRoom } from '../useRoom';
import { Hand } from './Hand';
import { JoinDialog } from './JoinDialog';
import { Results } from './Results';
import { RoomHeader } from './RoomHeader';
import { Table } from './Table';
import { ThrowLayer } from './ThrowLayer';
import { YouBar } from './YouBar';

type Identity = { clientId: string; name: string; spectator: boolean };

const initialIdentity = (): Identity | null => {
  const name = getStoredName().trim();
  return name ? { clientId: getClientId(), name, spectator: getStoredSpectator() } : null;
};

export const Room = ({ roomId }: { roomId: string }) => {
  const [identity, setIdentity] = useState<Identity | null>(initialIdentity);
  const { room, youId, status, error, throws, retireThrow, send, dismissError } = useRoom(
    roomId,
    identity,
  );
  const [recentEmoji, rememberEmoji] = useRecentEmoji();

  const me = useMemo(
    () => room?.members.find((member) => member.id === youId) ?? null,
    [room, youId],
  );
  const deck = useMemo(
    () => (room ? room.decks.find((entry) => entry.id === room.deckId) : undefined),
    [room],
  );

  if (!identity) {
    return (
      <JoinDialog
        initialName={getStoredName()}
        initialSpectator={getStoredSpectator()}
        onJoin={(name, spectator) => {
          storeName(name);
          storeSpectator(spectator);
          setIdentity({ clientId: getClientId(), name, spectator });
        }}
      />
    );
  }

  if (status === 'kicked') {
    return (
      <div className="overlay">
        <div className="dialog">
          <h1>You were removed</h1>
          <p className="dialog-sub">The host removed you from this session.</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/')}>
            Back to start
          </button>
        </div>
      </div>
    );
  }

  if (!room || !me) {
    return (
      <div className="overlay">
        <div className="dialog dialog-quiet">
          <p className="dialog-sub">Joining the table…</p>
        </div>
      </div>
    );
  }

  const isHost = room.hostId === youId;
  const anyVotes = room.members.some((member) => !member.spectator && member.hasVoted);

  const leave = () => {
    send({ t: 'leave' });
    navigate('/');
  };

  return (
    <div className="room">
      <RoomHeader
        topic={room.topic}
        canEditTopic={isHost}
        status={status}
        onTopicChange={(topic) => send({ t: 'setTopic', topic })}
      />

      <main className="room-main">
        <Table
          members={room.members}
          deck={deck}
          recentEmoji={recentEmoji}
          revealed={room.revealed}
          youId={youId}
          isHost={isHost}
          onKick={(memberId) => send({ t: 'kick', memberId })}
          onTransferHost={(memberId) => send({ t: 'transferHost', memberId })}
          onThrow={(targetId, emoji) => {
            rememberEmoji(emoji);
            send({ t: 'throw', targetId, emoji });
          }}
        />

        <div className="controls">
          {room.revealed ? (
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={() => send({ t: 'newRound' })}
            >
              New round
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-lg"
              disabled={!anyVotes}
              onClick={() => send({ t: 'reveal' })}
            >
              {anyVotes ? 'Reveal cards' : 'Waiting for votes…'}
            </button>
          )}
        </div>

        {room.revealed && room.stats && <Results stats={room.stats} />}
      </main>

      <footer className="room-footer">
        {me.spectator ? (
          <p className="spectating-note">You are spectating — switch off below to pick a card.</p>
        ) : (
          <Hand
            deck={deck}
            myVote={me.vote}
            disabled={room.revealed}
            onPick={(cardId) => send({ t: 'vote', cardId })}
          />
        )}

        <div className="footer-bar">
          <YouBar
            name={me.name}
            spectator={me.spectator}
            onRename={(name) => {
              storeName(name);
              send({ t: 'setName', name });
            }}
            onSpectatorChange={(spectator) => {
              storeSpectator(spectator);
              send({ t: 'setSpectator', spectator });
            }}
            onLeave={leave}
          />

          {isHost && (
            <div className="host-controls">
              <label className="field field-inline">
                <span>Deck</span>
                <select
                  value={room.deckId}
                  onChange={(event) => send({ t: 'setDeck', deckId: event.target.value })}
                >
                  {room.decks.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="checkbox checkbox-inline">
                <input
                  type="checkbox"
                  checked={room.autoReveal}
                  onChange={(event) => send({ t: 'setAutoReveal', enabled: event.target.checked })}
                />
                <span>Reveal automatically</span>
              </label>
            </div>
          )}
        </div>
      </footer>

      <ThrowLayer throws={throws} onRetire={retireThrow} />

      {error && (
        <div className="toast" role="alert">
          <span>{error}</span>
          <button type="button" className="icon-btn" onClick={dismissError} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}
    </div>
  );
};
